import React, { useEffect, useState, FormEvent } from "react";
import { Content, MainContainer } from "../../../styles/styles";
import { SideBar } from "../../../components/nav/SideBar";
import { useNavigationStore } from "store/navigationStore";
import {
  getUserProfile,
  updateUserProfile,
  updateUserPassword,
} from "../../../apis/user/profile";
import { FiLock, FiCopy, FiRefreshCcw, FiTrash2 } from "react-icons/fi";
import {
  AccountPageContainer,
  FormCard,
  FormSection,
  FormButton,
  PasswordHint,
  SectionTitle,
  SectionDescription,
  FieldRow,
  FieldLabel,
  FieldInputWrapper,
  ButtonGroup,
  SmallButton,
} from "./styles";
import { EnHeadline } from "styles/sharedStyles";
import { motion, AnimatePresence } from "framer-motion";
import RenderPasswordField from "../../../components/common/RenderPasswordField";
import { generateApiKey, deleteApiKey } from "apis/user/apiKey";
import { useModal } from "store/modalStore";
import UnifiedDropdown from "components/dropdown/UnifiedDropdown";
import { 
  INDUSTRY_NAMES,
  POSITION_NAMES,
  ROLE_NAMES,
  COMPANY_SIZE_NAMES,
  getIndustryCodeByName,
  getIndustryNameByCode,
  getPositionCodeByName,
  getPositionNameByCode,
  getRoleCodeByName,
  getRoleNameByCode,
  getCompanySizeCodeByName,
  getCompanySizeNameByCode
} from "constants/dropdownOptions";

const MyProfilePage: React.FC = () => {
  const { isCollapsed } = useNavigationStore(); 
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [position, setPosition] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // 새로 추가된 필드들
  const [selectedIndustry, setSelectedIndustry] = useState({ code: "", other: null as string | null });
  const [selectedIndustryOption, setSelectedIndustryOption] = useState("");
  const [selectedSize, setSelectedSize] = useState({ code: "", other: null });
  const [selectedSizeOption, setSelectedSizeOption] = useState("");
  const [jobPositionCode, setJobPositionCode] = useState("");
  const [selectedJobDuties, setSelectedJobDuties] = useState({ code: "", other: null as string | null });
  const [selectedJobDutiesOption, setSelectedJobDutiesOption] = useState("");
  // 커스텀 입력을 위한 상태 변수들
  const [industryCustom, setIndustryCustom] = useState("");
  const [positionCustom, setPositionCustom] = useState("");
  const [dutiesCustom, setDutiesCustom] = useState("");
  const [isPrivacy, setIsPrivacy] = useState(false);
  const [isMarketing, setIsMarketing] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const { openInfo } = useModal();

  // 직무 이름을 코드로 변환하는 함수
  const getJobPositionCodeByName = (name: string) => {
    return getPositionCodeByName(name);
  };

  // 코드를 직무 이름으로 변환하는 함수
  const getJobPositionNameByCode = (code: string) => {
    return getPositionNameByCode(code);
  };

  // 업무 내용 이름을 코드로 변환하는 함수
  const getJobDutiesCodeByName = (name: string) => {
    return getRoleCodeByName(name);
  };

  // 코드를 업무 내용 이름으로 변환하는 함수
  const getJobDutiesNameByCode = (code: string) => {
    return getRoleNameByCode(code);
  };
  const maskApiKey = (key: string) => {
    if (!key || key.length < 8) return key;
    const first = key.slice(0, 3);
    const last = key.slice(-4);
    const masked = "*".repeat(key.length - 7);
    return `${first}${masked}${last}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const rawProfile = await getUserProfile();
        
        // 기본 프로필 정보 설정
        setName(rawProfile.profile.name);
        setEmail(rawProfile.email);
        setCompany(rawProfile.company.name);
        setApiKey(rawProfile.membership?.key || "");
        
        // 새로운 필드들 설정
        if (rawProfile.company.industry) {
          const industryCode = rawProfile.company.industry.code || "";
          const industryOther = rawProfile.company.industry.other || "";
          
          setSelectedIndustry({
            code: industryCode,
            other: industryOther || null
          });
          
          // 업종 코드를 이름으로 변환해서 설정
          const industryName = getIndustryNameByCode(industryCode);
          if (industryName) {
            setSelectedIndustryOption(industryName);
            // "기타"인 경우 커스텀 값 설정
            if (industryCode === "other" && industryOther) {
              setIndustryCustom(industryOther);
            }
          }
        }
        
        if (rawProfile.company.size) {
          const sizeCode = rawProfile.company.size.code || "";
          setSelectedSize({
            code: sizeCode,
            other: null
          });
          
          // 회사 크기 코드를 이름으로 변환해서 설정
          const sizeName = getCompanySizeNameByCode(sizeCode);
          if (sizeName) {
            setSelectedSizeOption(sizeName);
          }
        }
        
        if (rawProfile.company.jobPosition) {
          const jobPositionCode = rawProfile.company.jobPosition.code || "";
          const jobPositionName = rawProfile.company.jobPosition.name || "";
          const jobPositionOther = rawProfile.company.jobPosition.other || "";
          
          setPosition(jobPositionName);
          setJobPositionCode(jobPositionCode);
          
          // 코드를 통해 올바른 이름을 찾아서 설정 (코드가 더 신뢰할 수 있음)
          const convertedName = getJobPositionNameByCode(jobPositionCode);
          if (convertedName) {
            setSelectedOption(convertedName);
            // "기타"인 경우 커스텀 값 설정
            if (jobPositionCode === "other" && jobPositionOther) {
              setPositionCustom(jobPositionOther);
            }
          }
        }
        
        if (rawProfile.company.jobDuties) {
          const jobDutiesCode = rawProfile.company.jobDuties.code || "";
          const jobDutiesOther = rawProfile.company.jobDuties.other || "";
          
          setSelectedJobDuties({
            code: jobDutiesCode,
            other: jobDutiesOther || null
          });
          
          // 업무 내용 코드를 이름으로 변환해서 설정
          const jobDutiesName = getJobDutiesNameByCode(jobDutiesCode);
          if (jobDutiesName) {
            setSelectedJobDutiesOption(jobDutiesName);
            // "기타"인 경우 커스텀 값 설정
            if (jobDutiesCode === "other" && jobDutiesOther) {
              setDutiesCustom(jobDutiesOther);
            }
          }
        }
        
        if (rawProfile.consent) {
          setIsPrivacy(rawProfile.consent.isPrivacy);
          setIsMarketing(rawProfile.consent.isMarketing);
        }


      } catch (error) {
        console.error("Failed to fetch profile:", error);
      }
    })();
  }, []);

  // jobPositionCode가 변경될 때 selectedOption 설정
  useEffect(() => {
    if (jobPositionCode && !selectedOption) {
      const convertedName = getJobPositionNameByCode(jobPositionCode);
      if (convertedName) {
        setSelectedOption(convertedName);
      }
    }
  }, [jobPositionCode, selectedOption]);

  const handleUpdateProfile = async (e: FormEvent) => {
    e.preventDefault();
    try {
      // 선택된 직무 이름을 코드로 변환 (selectedOption은 직무 선택에 사용됨)
      const selectedJobPositionCode = selectedOption ? getJobPositionCodeByName(selectedOption) : jobPositionCode;
      
      // 선택된 업종 이름을 코드로 변환
      const selectedIndustryCode = selectedIndustryOption ? getIndustryCodeByName(selectedIndustryOption) : selectedIndustry.code;
      
      // 선택된 업무 내용 이름을 코드로 변환
      const selectedJobDutiesCode = selectedJobDutiesOption ? getJobDutiesCodeByName(selectedJobDutiesOption) : selectedJobDuties.code;
      
      // 선택된 회사 크기 이름을 코드로 변환
      const selectedSizeCode = selectedSizeOption ? getCompanySizeCodeByName(selectedSizeOption) : selectedSize.code;
      
      const res = await updateUserProfile({
        username: name,
        organization: company,
        jobPosition: selectedJobPositionCode === "other" ? positionCustom : (selectedOption || position),
        industry: {
          code: selectedIndustryCode,
          other: selectedIndustryCode === "other" ? industryCustom : null
        },
        size: {
          code: selectedSizeCode,
          other: selectedSize.other
        },
        jobPositionCode: selectedJobPositionCode,
        jobDuties: {
          code: selectedJobDutiesCode,
          other: selectedJobDutiesCode === "other" ? dutiesCustom : null
        },
        consent: {
          isPrivacy: isPrivacy,
          isMarketing: isMarketing,
        },
      });
      if (res.success) openInfo({ title: "프로필이 업데이트되었습니다.", message: "" });
    } catch {
      openInfo({ title: "업데이트 실패", message: "프로필 업데이트 중 오류가 발생했습니다." });
    }
  };

  const handleUpdatePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError(false);

    if (!currentPassword || !newPassword || !confirmPassword) {
      openInfo({ title: "입력 오류", message: "모든 비밀번호 필드를 입력해주세요." });
      return;
    }
    if (newPassword !== confirmPassword) {
      openInfo({ title: "불일치", message: "새 비밀번호와 확인 비밀번호가 일치하지 않습니다." });
      return;
    }
    if (currentPassword === newPassword) {
      openInfo({ title: "중복 비밀번호", message: "기존 비밀번호와 새 비밀번호는 동일할 수 없습니다." });
      return;
    }

    try {
      await updateUserPassword({ currentPassword, newPassword, confirmPassword });
      openInfo({ title: "성공", message: "비밀번호가 변경되었습니다." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordForm(false);
    } catch (error: any) {
      if (error?.response?.status === 620) setPasswordError(true);
      else openInfo({ title: "오류", message: "비밀번호 변경 중 문제가 발생했습니다." });
    }
  };

  const handleCopyKey = async () => {
    await navigator.clipboard.writeText(apiKey);
    openInfo({ title: "복사 완료", message: "API Key가 복사되었습니다." });
  };

  const handleReissueKey = async () => {
    try {
      const newKey = await generateApiKey();
      setApiKey(newKey);
    } catch (error) {
      openInfo({ title: "오류", message: "API Key 재발급 중 문제가 발생했습니다." });
    }
  };

  const handleDeleteKey = async () => {
    // API Key가 유효한지 검증
    if (!apiKey || apiKey.trim() === "" || apiKey === "undefined") {
      openInfo({ title: "안내", message: "삭제할 API Key가 없습니다." });
      return;
    }

    try {
      await deleteApiKey(apiKey);
      openInfo({
        title: "삭제 완료",
        message: "API Key가 성공적으로 삭제되었습니다."
      });
      setApiKey("");
    } catch (error: any) {
      console.error("Failed to delete API key:", error);
      
      // API 응답 구조에 따른 에러 처리
      const errorData = error?.response?.data;
      
      if (errorData && errorData.message) {
        // 서버에서 받은 에러 메시지 사용
        openInfo({ title: "삭제 실패", message: errorData.message });
      } else if (error?.response?.status === 400) {
        openInfo({ title: "오류", message: "유효하지 않은 API Key입니다." });
      } else if (error?.response?.status === 404) {
        openInfo({ title: "오류", message: "API Key를 찾을 수 없습니다." });
      } else if (error?.response?.status === 401) {
        openInfo({ title: "인증 오류", message: "권한이 없습니다." });
      } else {
        openInfo({ title: "오류", message: "API Key 삭제 중 문제가 발생했습니다." });
      }
    }
  };

  return (
    <MainContainer>
      <Content $isCollapsed={isCollapsed}>
        <SideBar />
        <AccountPageContainer $isCollapsed={isCollapsed}>
          <EnHeadline style={{ alignSelf: "flex-start" }}>내 계정 관리</EnHeadline>

          {/* 프로필 정보 */}
          <FormCard>
            <SectionTitle>프로필 정보</SectionTitle>
            <FormSection onSubmit={handleUpdateProfile}>
              <FieldRow>
                <FieldLabel>이름 / 닉네임</FieldLabel>
                <FieldInputWrapper>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="이름"
                  />
                </FieldInputWrapper>
              </FieldRow>

              <FieldRow>
                <FieldLabel>이메일</FieldLabel>
                <FieldInputWrapper>
                  <input type="email" value={email} disabled placeholder="이메일" />
                </FieldInputWrapper>
              </FieldRow>

              <FieldRow>
                <FieldLabel>소속</FieldLabel>
                <FieldInputWrapper>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="소속"
                  />
                </FieldInputWrapper>
              </FieldRow>

              <FieldRow>
                <FieldLabel>직무</FieldLabel>
                <FieldInputWrapper>
                  <UnifiedDropdown
                    label="직무"
                    dropdownName="position"
                    options={POSITION_NAMES}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    customValue={positionCustom}
                    setCustomValue={setPositionCustom}
                    placeholder="직무를 선택해주세요"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                  />
                </FieldInputWrapper>
              </FieldRow>

              <FieldRow>
                <FieldLabel>업종</FieldLabel>
                <FieldInputWrapper>
                  <UnifiedDropdown
                    label="업종"
                    dropdownName="industry"
                    options={INDUSTRY_NAMES}
                    selectedOption={selectedIndustryOption}
                    setSelectedOption={setSelectedIndustryOption}
                    customValue={industryCustom}
                    setCustomValue={setIndustryCustom}
                    placeholder="업종을 선택해주세요"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                  />
                </FieldInputWrapper>
              </FieldRow>

              <FieldRow>
                <FieldLabel>회사 크기</FieldLabel>
                <FieldInputWrapper>
                  <UnifiedDropdown
                    label="회사 크기"
                    dropdownName="companySize"
                    options={COMPANY_SIZE_NAMES}
                    selectedOption={selectedSizeOption}
                    setSelectedOption={setSelectedSizeOption}
                    placeholder="회사 크기를 선택해주세요"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    showCustomInput={false}
                  />
                </FieldInputWrapper>
              </FieldRow>

              <FieldRow>
                <FieldLabel>업무 내용</FieldLabel>
                <FieldInputWrapper>
                  <UnifiedDropdown
                    label="업무 내용"
                    dropdownName="jobDuties"
                    options={ROLE_NAMES}
                    selectedOption={selectedJobDutiesOption}
                    setSelectedOption={setSelectedJobDutiesOption}
                    customValue={dutiesCustom}
                    setCustomValue={setDutiesCustom}
                    placeholder="업무내용을 선택해주세요"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                  />
                </FieldInputWrapper>
              </FieldRow>

              <FormButton style={{ marginTop: "24px" }} type="submit">프로필 저장</FormButton>
            </FormSection>
          </FormCard>

          {/* API Key 관리 */}
          <FormCard>
            <SectionTitle>
              API Key
            </SectionTitle>
            <SectionDescription>
              API Key는 외부 서비스 연동 시 사용됩니다.
            </SectionDescription>

            <FieldRow>
              <FieldLabel>현재 Key</FieldLabel>
              <FieldInputWrapper>
                <input
                  type="text"
                  value={maskApiKey(apiKey)}
                  readOnly
                  style={{ background: "#f6f7fb", color: "#555" }}
                />
              </FieldInputWrapper>
            </FieldRow>

            <ButtonGroup>
              <SmallButton onClick={handleCopyKey}>
                <FiCopy /> 복사
              </SmallButton>
              <SmallButton onClick={handleReissueKey}>
                <FiRefreshCcw /> 재발급
              </SmallButton>
              <SmallButton $danger onClick={handleDeleteKey}>
                <FiTrash2 /> 삭제
              </SmallButton>
            </ButtonGroup>
          </FormCard>

          {/* 계정 보안 */}
          <FormCard style={{ background: "linear-gradient(180deg, #fafbff 0%, #ffffff 100%)" }}>
            <SectionTitle>
              <FiLock style={{ marginRight: "6px" }} /> 계정 보안
            </SectionTitle>
            <SectionDescription>
              비밀번호를 변경하여 계정을 안전하게 보호하세요.
            </SectionDescription>

            <FormButton
              type="button"
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              style={{
                width: "fit-content",
                padding: "8px 20px",
                marginBottom: "10px",
                background: showPasswordForm ? "#8791ff" : "#5c6eff",
              }}
            >
              {showPasswordForm ? "비밀번호 변경 닫기" : "비밀번호 변경하기"}
            </FormButton>

            <AnimatePresence>
              {showPasswordForm && (
                <motion.div
                  key="passwordForm"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <FormSection onSubmit={handleUpdatePassword}>
                    <RenderPasswordField
                      name="currentPassword"
                      label="현재 비밀번호"
                      placeholder="기존 비밀번호"
                      error={passwordError}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                    <RenderPasswordField
                      name="newPassword"
                      label="새 비밀번호"
                      placeholder="새로운 비밀번호"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      hint={
                        <PasswordHint>
                          영어, 숫자, 특수문자를 조합하여 8~12자 이내로 설정해주세요.
                        </PasswordHint>
                      }
                    />
                    <RenderPasswordField
                      name="confirmPassword"
                      label="비밀번호 확인"
                      placeholder="비밀번호 확인"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <FormButton type="submit">비밀번호 저장</FormButton>
                  </FormSection>
                </motion.div>
              )}
            </AnimatePresence>
          </FormCard>
        </AccountPageContainer>
      </Content>
    </MainContainer>
  );
};

export default MyProfilePage;
